import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { NocturneClubRuntime } from "./NocturneClubRuntime";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function NocturneClubStyles() {
    return (
        <style>{`
            [data-web-template="nocturne-club"] {
                background:
                    linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,.018) 1px, transparent 1px),
                    radial-gradient(circle at 22% 20%, color-mix(in srgb, var(--site-primary) 12%, transparent), transparent 30rem),
                    radial-gradient(circle at 78% 28%, color-mix(in srgb, var(--site-accent) 11%, transparent), transparent 32rem),
                    var(--site-background);
                background-size: 64px 64px, 64px 64px, auto, auto, auto;
                isolation: isolate;
                scroll-behavior: smooth;
                scrollbar-color: var(--site-primary) rgba(255,255,255,.08);
                scrollbar-width: thin;
            }

            [data-web-template="nocturne-club"] ::selection {
                background: var(--site-primary);
                color: var(--site-background);
            }

            [data-web-template="nocturne-club"]::-webkit-scrollbar {
                width: 12px;
            }

            [data-web-template="nocturne-club"]::-webkit-scrollbar-track {
                background: rgba(255,255,255,.055);
            }

            [data-web-template="nocturne-club"]::-webkit-scrollbar-thumb {
                background: linear-gradient(180deg, color-mix(in srgb, var(--site-primary) 72%, white), var(--site-primary));
                border: 3px solid color-mix(in srgb, var(--site-background) 96%, black);
                border-radius: 999px;
                box-shadow: 0 0 16px color-mix(in srgb, var(--site-primary) 38%, transparent);
            }

            [data-web-template="nocturne-club"] .site-builder-section,
            [data-web-template="nocturne-club"] [data-site-builder-block="footer"] {
                box-sizing: border-box;
                padding-inline: 1.25rem;
                position: relative;
            }

            [data-web-template="nocturne-club"] .site-builder-grid {
                display: grid;
                gap: clamp(1.25rem, 2.8vw, 2.65rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="nocturne-club"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="nocturne-club"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="nocturne-club"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.15rem;
                min-width: 0;
            }

            [data-web-template="nocturne-club"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="nocturne-club"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2.2vw, 2.1rem);
                justify-content: center;
            }

            [data-web-template="nocturne-club"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="nocturne-club"] .site-builder-navigation-link,
            [data-web-template="nocturne-club"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                width: auto !important;
            }

            [data-web-template="nocturne-club"] .site-builder-navigation-link::after,
            [data-web-template="nocturne-club"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--site-primary);
                bottom: -.35rem;
                box-shadow: 0 0 16px color-mix(in srgb, var(--site-primary) 80%, transparent);
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                right: 0;
                transform: scaleX(0);
                transform-origin: right;
                transition: transform 180ms ease;
            }

            [data-web-template="nocturne-club"] .site-builder-navigation-link:hover::after,
            [data-web-template="nocturne-club"] [data-document-role="headerNavigation"] .site-builder-text:hover::after {
                transform: scaleX(1);
                transform-origin: left;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"] {
                background: linear-gradient(180deg, rgba(0,0,0,.64), rgba(0,0,0,.14) 76%, transparent) !important;
                left: 0;
                position: fixed;
                right: 0;
                top: 0;
                transition: background 420ms ease, box-shadow 420ms ease, min-height 420ms ease, padding 420ms ease, transform 420ms ease;
                width: 100%;
                z-index: 40;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
                transition: gap 420ms ease;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="brand"] {
                justify-content: center;
                transition: opacity 420ms ease, transform 420ms ease;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                justify-content: center;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="headerCta"],
            [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] {
                backdrop-filter: blur(18px);
                background: color-mix(in srgb, var(--site-background) 84%, transparent) !important;
                border-bottom-color: var(--site-border) !important;
                box-shadow: 0 18px 55px rgba(0,0,0,.3);
                min-height: 72px !important;
                padding-block: 12px !important;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] > .site-builder-grid {
                grid-auto-rows: auto !important;
                grid-template-columns: 3fr 6fr 3fr !important;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                grid-column: 1 / span 3 !important;
                grid-row: 1 !important;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                grid-column: 4 / span 6 !important;
                grid-row: 1 !important;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                grid-column: 10 / span 3 !important;
                grid-row: 1 !important;
            }

            [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] [data-document-role="brand"] .site-builder-text {
                font-size: clamp(26px, 3vw, 38px) !important;
            }

            [data-web-template="nocturne-club"] [data-web-region="homeHero"] {
                isolation: isolate;
                min-height: 100svh;
                overflow: hidden;
                padding-inline: 0;
            }

            [data-web-template="nocturne-club"] [data-web-region="homeHero"]::after {
                background:
                    linear-gradient(90deg, rgba(3,4,8,.44), rgba(3,4,8,.06) 48%, rgba(3,4,8,.32)),
                    linear-gradient(180deg, rgba(3,4,8,.02), rgba(3,4,8,.52));
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="nocturne-club"] [data-web-region="homeHero"] > .site-builder-grid {
                inset: 0;
                max-width: none;
                position: absolute;
                width: 100%;
            }

            [data-web-template="nocturne-club"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroSlideshow"]) {
                grid-column: 1 / -1 !important;
                grid-row: 1 / -1 !important;
                inset: 0;
                position: absolute;
                z-index: 0;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroSlideshow"] {
                inset: 0;
                position: absolute;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroSlide"] {
                inset: 0;
                opacity: 0;
                position: absolute;
                transform: scale(1.018);
                transition: opacity 1500ms ease, transform 7600ms linear;
                width: 100%;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroSlide"] .site-builder-image {
                filter: saturate(1.08) contrast(1.04);
                height: 100% !important;
                max-width: none;
                min-height: 100svh !important;
                object-fit: cover;
                width: 100% !important;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroSlide"][data-nocturne-slide-active] {
                opacity: 1;
                transform: scale(1);
                z-index: 1;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroScrollCue"] {
                align-items: center;
                backdrop-filter: blur(9px);
                background: rgba(3,4,8,.2);
                border: 1px solid color-mix(in srgb, var(--site-primary) 44%, transparent);
                border-radius: 999px;
                bottom: clamp(2rem, 5vh, 3.8rem);
                box-shadow: 0 0 26px color-mix(in srgb, var(--site-primary) 12%, transparent), inset 0 0 18px color-mix(in srgb, var(--site-primary) 4%, transparent);
                display: flex;
                justify-content: center;
                left: 50%;
                min-height: 48px;
                opacity: var(--nocturne-scroll-cue-opacity, 1);
                padding: .68rem 1rem;
                position: absolute;
                transform: translateX(-50%) translateY(calc((1 - var(--nocturne-scroll-cue-opacity, 1)) * 12px));
                transition: border-color 180ms ease, background 180ms ease;
                width: fit-content;
                z-index: 3;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroScrollCue"]::before {
                animation: nocturne-scroll-cue 1.4s ease-in-out infinite;
                background: var(--site-primary);
                border-radius: 999px;
                box-shadow: 0 0 12px color-mix(in srgb, var(--site-primary) 80%, transparent);
                content: "";
                height: 6px;
                width: 3px;
            }

            [data-web-template="nocturne-club"] [data-document-role="heroScrollCue"][data-nocturne-scroll-cue-hidden] {
                pointer-events: none;
            }

            [data-web-template="nocturne-club"] [data-web-region="homeIntro"] {
                background: linear-gradient(180deg, rgba(0,0,0,.82), color-mix(in srgb, var(--site-background) 96%, black)) !important;
            }

            [data-web-template="nocturne-club"] [data-document-role="editorialVisual"],
            [data-web-template="nocturne-club"] [data-document-role="galleryTile"] {
                overflow: hidden;
                position: relative;
            }

            [data-web-template="nocturne-club"] [data-document-role="galleryTile"] {
                cursor: zoom-in;
                outline: 0;
            }

            [data-web-template="nocturne-club"] [data-document-role="galleryTile"]:focus-visible {
                box-shadow: 0 0 0 3px var(--site-background), 0 0 0 5px var(--site-primary), 0 0 28px color-mix(in srgb, var(--site-primary) 34%, transparent);
            }

            [data-web-template="nocturne-club"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="nocturne-club"] [data-document-role="galleryTile"] .site-builder-image {
                min-height: 100%;
                transition: filter 420ms ease, transform 420ms ease;
            }

            [data-web-template="nocturne-club"] [data-document-role="editorialVisual"]:hover .site-builder-image,
            [data-web-template="nocturne-club"] [data-document-role="galleryTile"]:hover .site-builder-image {
                filter: saturate(1.16) contrast(1.06);
                transform: scale(1.045);
            }

            [data-web-template="nocturne-club"] [data-animation="reveal"] {
                opacity: 0;
                transform: translateY(18px);
                transition: opacity 520ms ease, transform 520ms ease;
            }

            [data-web-template="nocturne-club"] [data-animation="reveal"][data-nocturne-revealed] {
                opacity: 1;
                transform: translateY(0);
            }

            [data-web-template="nocturne-club"] [data-web-region="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .7rem;
            }

            [data-web-template="nocturne-club"] .nocturne-mobile-menu-toggle {
                display: none;
            }

            .nocturne-gallery-lightbox {
                align-items: center;
                animation: nocturne-gallery-enter 220ms ease-out both;
                display: flex;
                inset: 0;
                justify-content: center;
                padding: clamp(.7rem, 2vw, 1.5rem);
                position: fixed;
                z-index: 1000;
            }

            .nocturne-gallery-backdrop {
                background:
                    radial-gradient(circle at 20% 18%, color-mix(in srgb, var(--site-primary) 18%, transparent), transparent 32rem),
                    radial-gradient(circle at 82% 22%, color-mix(in srgb, var(--site-accent) 17%, transparent), transparent 34rem),
                    rgba(0,0,0,.9);
                backdrop-filter: blur(18px);
                border: 0;
                cursor: zoom-out;
                inset: 0;
                position: absolute;
            }

            .nocturne-gallery-shell {
                animation: nocturne-gallery-shell-enter 220ms ease-out both;
                display: grid;
                max-height: calc(100svh - 2rem);
                max-width: 1160px;
                position: relative;
                width: 100%;
                z-index: 1;
            }

            .nocturne-gallery-figure {
                display: grid;
                gap: .75rem;
                grid-template-rows: minmax(0, 1fr) auto;
                margin: 0;
                min-height: 0;
                touch-action: pan-y;
            }

            .nocturne-gallery-image-wrap {
                align-items: center;
                background: linear-gradient(135deg, rgba(255,255,255,.07), rgba(255,255,255,.015)), rgba(3,4,8,.85);
                border: 1px solid rgba(255,255,255,.15);
                border-radius: 4px;
                box-shadow: 0 28px 88px rgba(0,0,0,.52);
                display: flex;
                height: min(78svh, 760px);
                justify-content: center;
                min-height: 0;
                overflow: hidden;
            }

            .nocturne-gallery-image {
                max-height: 100%;
                max-width: 100%;
                object-fit: contain;
                user-select: none;
            }

            .nocturne-gallery-meta {
                align-items: center;
                color: rgba(255,255,255,.7);
                display: flex;
                font-family: Inter, system-ui, sans-serif;
                font-size: .9rem;
                gap: 1rem;
                justify-content: space-between;
            }

            .nocturne-gallery-meta > span {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .nocturne-gallery-meta > strong {
                color: #19d7e5;
                font-family: "Barlow Condensed", "Arial Narrow", Oswald, Impact, sans-serif;
                letter-spacing: .14em;
                white-space: nowrap;
            }

            .nocturne-gallery-close,
            .nocturne-gallery-nav {
                align-items: center;
                backdrop-filter: blur(12px);
                background: rgba(3,4,8,.75);
                border: 1px solid rgba(255,255,255,.2);
                border-radius: 999px;
                box-shadow: 0 14px 42px rgba(0,0,0,.38);
                color: #fff;
                cursor: pointer;
                display: inline-flex;
                justify-content: center;
                position: absolute;
                transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
                z-index: 3;
            }

            .nocturne-gallery-close:hover,
            .nocturne-gallery-close:focus-visible,
            .nocturne-gallery-nav:hover,
            .nocturne-gallery-nav:focus-visible {
                background: color-mix(in srgb, #19d7e5 15%, rgba(3,4,8,.82));
                border-color: color-mix(in srgb, #19d7e5 70%, transparent);
                outline: 0;
            }

            .nocturne-gallery-close {
                font-size: 1.75rem;
                height: 2.9rem;
                line-height: 1;
                right: .75rem;
                top: .75rem;
                width: 2.9rem;
            }

            .nocturne-gallery-nav {
                font-size: 2.4rem;
                height: clamp(2.9rem, 5vw, 3.7rem);
                line-height: 1;
                top: 50%;
                transform: translateY(-50%);
                width: clamp(2.9rem, 5vw, 3.7rem);
            }

            .nocturne-gallery-nav:hover,
            .nocturne-gallery-nav:focus-visible {
                transform: translateY(-50%) scale(1.04);
            }

            .nocturne-gallery-nav-prev {
                left: .75rem;
            }

            .nocturne-gallery-nav-next {
                right: .75rem;
            }

            @keyframes nocturne-scroll-cue {
                0%, 100% { opacity: .35; transform: translateY(-4px); }
                45% { opacity: 1; transform: translateY(4px); }
            }

            @keyframes nocturne-gallery-enter {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            @keyframes nocturne-gallery-shell-enter {
                from { opacity: 0; transform: translateY(14px) scale(.985); }
                to { opacity: 1; transform: translateY(0) scale(1); }
            }

            @container (max-width: 900px) {
                [data-web-template="nocturne-club"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="nocturne-club"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] {
                    backdrop-filter: none;
                    background: transparent !important;
                    border-bottom-color: transparent !important;
                    min-height: 118px !important;
                    padding: 18px 1rem !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] > .site-builder-grid {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]),
                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="brand"] {
                    justify-content: center;
                    pointer-events: none;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] [data-document-role="brand"] .site-builder-text {
                    font-size: clamp(42px, 13vw, 58px) !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] {
                    backdrop-filter: blur(14px);
                    background: color-mix(in srgb, var(--site-background) 82%, transparent) !important;
                    border-bottom-color: var(--site-border) !important;
                    min-height: 74px !important;
                    padding: 14px 1rem !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] > .site-builder-grid {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] [data-document-role="brand"] {
                    justify-content: flex-start;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] [data-document-role="brand"] .site-builder-text {
                    font-size: 31px !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="nocturne-club"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"] .nocturne-mobile-menu-toggle {
                    align-items: center;
                    backdrop-filter: blur(14px);
                    background: color-mix(in srgb, var(--site-surface) 74%, transparent);
                    border: 1px solid color-mix(in srgb, var(--site-primary) 42%, transparent);
                    border-radius: 3px;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    height: 44px;
                    justify-content: center;
                    padding: 0;
                    position: absolute;
                    right: 1rem;
                    top: 15px;
                    width: 44px;
                    z-index: 8;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-header-scrolled] .nocturne-mobile-menu-toggle,
                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] .nocturne-mobile-menu-toggle {
                    display: flex;
                }

                [data-web-template="nocturne-club"] .nocturne-mobile-menu-toggle > span {
                    background: var(--site-primary);
                    border-radius: 999px;
                    box-shadow: 0 0 9px color-mix(in srgb, var(--site-primary) 56%, transparent);
                    display: block;
                    height: 2px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 21px;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] {
                    animation: nocturne-mobile-menu-reveal 220ms ease-out;
                    background:
                        radial-gradient(circle at 90% 8%, color-mix(in srgb, var(--site-primary) 14%, transparent), transparent 30rem),
                        radial-gradient(circle at 8% 86%, color-mix(in srgb, var(--site-accent) 12%, transparent), transparent 28rem),
                        var(--site-background) !important;
                    bottom: 0;
                    height: 100dvh;
                    inset-inline: 0;
                    overflow-y: auto;
                    position: fixed;
                    top: 0;
                    width: 100%;
                    z-index: 100;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] > .site-builder-grid {
                    align-content: stretch;
                    grid-template-columns: minmax(0, 1fr) auto !important;
                    grid-template-rows: auto minmax(0, 1fr) auto !important;
                    min-height: calc(100dvh - 28px);
                    width: min(100%, 42rem);
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    align-self: center;
                    animation: nocturne-mobile-menu-content 280ms ease-out both;
                    grid-row: 2 !important;
                    padding-block: clamp(2rem, 8dvh, 5rem);
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    align-self: end;
                    animation: nocturne-mobile-menu-content 280ms ease-out both;
                    grid-row: 3 !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                    width: 100%;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding: clamp(.9rem, 2.5dvh, 1.2rem) 0;
                    width: 100%;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2.2rem, 10vw, 3.6rem) !important;
                    font-weight: 900 !important;
                    letter-spacing: .04em !important;
                    width: 100% !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerActions"] {
                    border-top: 1px solid var(--site-border);
                    gap: .75rem !important;
                    padding-top: 1rem;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] .nocturne-mobile-menu-toggle > span:first-child {
                    transform: translateY(7px) rotate(45deg);
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] .nocturne-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="nocturne-club"] [data-web-region="header"][data-nocturne-mobile-menu-open] .nocturne-mobile-menu-toggle > span:last-child {
                    transform: translateY(-7px) rotate(-45deg);
                }

                @keyframes nocturne-mobile-menu-reveal {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes nocturne-mobile-menu-content {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            }

            @container (max-width: 640px) {
                [data-web-template="nocturne-club"] {
                    background-size: 44px 44px, 44px 44px, auto, auto, auto;
                }

                [data-web-template="nocturne-club"] .site-builder-section,
                [data-web-template="nocturne-club"] [data-site-builder-block="footer"] {
                    padding-inline: 1rem;
                }

                [data-web-template="nocturne-club"] [data-web-region="homeHero"] {
                    padding-inline: 0;
                }

                [data-web-template="nocturne-club"] [data-document-role="heroSlide"] .site-builder-image {
                    object-position: 38% center !important;
                }

                [data-web-template="nocturne-club"] [data-document-role="heroScrollCue"] {
                    min-height: 44px;
                    padding: .58rem .75rem;
                }

                [data-web-template="nocturne-club"] [data-document-role="heroScrollCue"] .site-builder-text {
                    font-size: 0 !important;
                }

                [data-web-template="nocturne-club"] [data-document-role="heroScrollCue"] .site-builder-text::after {
                    content: "↓";
                    font-size: 15px;
                }

                [data-web-template="nocturne-club"] [data-document-role="heroHeading"].site-builder-text-h1,
                [data-web-template="nocturne-club"] [data-document-role="heroHeading"] .site-builder-text-h1 {
                    font-size: clamp(51px, 14.5vw, 72px) !important;
                }

                [data-web-template="nocturne-club"] .site-builder-column {
                    grid-template-columns: minmax(0, 1fr) !important;
                }
            }

            @media (max-width: 640px) {
                .nocturne-gallery-lightbox {
                    padding: .55rem;
                }

                .nocturne-gallery-shell {
                    max-height: calc(100svh - 1.1rem);
                }

                .nocturne-gallery-image-wrap {
                    height: calc(100svh - 5.5rem);
                }

                .nocturne-gallery-nav {
                    height: 2.8rem;
                    width: 2.8rem;
                }

                .nocturne-gallery-nav-prev {
                    left: .4rem;
                }

                .nocturne-gallery-nav-next {
                    right: .4rem;
                }

                .nocturne-gallery-meta {
                    padding-inline: .15rem;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="nocturne-club"] *,
                [data-web-template="nocturne-club"] *::before,
                [data-web-template="nocturne-club"] *::after {
                    animation-duration: .01ms !important;
                    animation-iteration-count: 1 !important;
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="nocturne-club"] [data-animation="reveal"] {
                    opacity: 1;
                    transform: none;
                }

                .nocturne-gallery-close,
                .nocturne-gallery-nav,
                .nocturne-gallery-lightbox,
                .nocturne-gallery-shell {
                    animation: none;
                    transition: none;
                }
            }
        `}</style>
    );
}

function NocturneFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-24 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:leading-[.86] [&_h1]:tracking-wide [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const nocturneClubTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "nocturne-club",
        name: "Noční klub",
        type: "website",
        category: "entertainment",
        description: "Temná obrazová šablona pro noční kluby, hudební bary a víkendové eventové prostory s filmovým hero slideshow.",
        previewImage: "/templates/nocturne-club/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["nocturne-club"],
        pageTypes: {
            home: {
                label: "Klubová homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Klubová obsahová stránka",
                slots: ["richContent"],
            },
            contact: {
                label: "Klubový kontakt",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            nocturneClubTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="nocturne-club"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <NocturneClubStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <NocturneClubRuntime
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                        designCssVars={templateDesignToCssVars(design)}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <NocturneFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
